/* eslint-disable quotes */
'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/app/context/language-context';
import {
  Check,
  ArrowRight,
  Home,
  DollarSign,
  Key,
  Award,
  FileText,
  Users,
  Wrench,
} from 'lucide-react';
import Link from 'next/link';

function Services() {
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const { t } = useLanguage();

  // Prevent hydration issues
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  // if (!isMounted) return null;

  return (
    <section className='relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20'>
      {/* SEO content for search engines */}
      <h1 className='sr-only'>
        {t('our_services') ||
          'Dubai Real Estate Services – Buy, Sell, Rent & Investment Solutions'}
      </h1>

      <h2 className='sr-only'>X-Space Real Estate Broker | Services</h2>

      <p className='sr-only'>
        {t('our_services_description') || 'X-Space Real Estate ...'}
      </p>

      {/* Animated Background & Motion Elements */}
      {isMounted && (
        <div className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>
          {/* Hero Section */}
          <section className='relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20'>
            {/* Background with animated gradient */}
            <div className='absolute inset-0 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 z-0'>
              <motion.div
                className='absolute inset-0 opacity-30 dark:opacity-40'
                animate={{
                  background: [
                    'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                    'radial-gradient(circle at 80% 70%, rgba(56, 245, 239, 0.15) 0%, rgba(254, 42, 83, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                    'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                  ],
                }}
                transition={{
                  duration: 15,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: 'easeInOut',
                }}
              />
            </div>

            {/* Decorative elements */}
            <div className='absolute inset-0 overflow-hidden z-0'>
              {/* Top left circle */}
              <motion.div
                className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-primarycolor/5 dark:bg-primarycolor/10'
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 10, 0],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 20,
                  ease: 'easeInOut',
                }}
              />

              {/* Bottom right circle */}
              <motion.div
                className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-secondarycolor/5 dark:bg-secondarycolor/10'
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 0],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 25,
                  ease: 'easeInOut',
                }}
              />

              {/* Small floating elements */}
              {[...Array(10)].map((_, i) => (
                <motion.div
                  key={`floating-${i}`}
                  className={`absolute w-${(i % 3) + 2} h-${(i % 3) + 2} rounded-full ${
                    i % 3 === 0
                      ? 'bg-primarycolor/20'
                      : i % 3 === 1
                        ? 'bg-secondarycolor/20'
                        : 'bg-[#D4AF37]/20'
                  }`}
                  style={{
                    top: `${10 + i * 8}%`,
                    left: `${5 + i * 9}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    x: [0, 10, 0],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    repeat: Number.POSITIVE_INFINITY,
                    duration: 5 + i,
                    ease: 'easeInOut',
                    delay: i * 0.3,
                  }}
                />
              ))}
            </div>

            <div className='container mx-auto px-4 z-10 py-20'>
              <div className='max-w-7xl mx-auto'>
                {/* Section title with animated underline */}
                <motion.div
                  className='text-center mb-16'
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <motion.h1
                    className='text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primarycolor to-secondarycolor bg-clip-text text-transparent'
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    itemProp='services with xspace'
                  >
                    {t('our_services') ||
                      'Dubai Real Estate Services – Buy, Sell, Rent & Investment Solutions'}
                  </motion.h1>
                  <motion.div
                    className='h-1 w-24 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto'
                    initial={{ width: 0 }}
                    animate={{ width: 120 }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                  <motion.p
                    className='mt-6 text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                  >
                    {t('our_services_description') ||
                      'X-Space Real Estate provides professional real estate services in Dubai including property buying, selling, renting, mortgage consultancy, off-plan investment, Golden Visa assistance, and complete property support for residential and commercial clients.'}
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Why List with XSpace Section */}
          <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900 relative overflow-hidden'>
            {/* Luxury pattern background */}
            <div className='absolute inset-0 opacity-5 dark:opacity-10'>
              <motion.div
                className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                animate={{
                  backgroundPosition: ['0% 0%', '100% 100%'],
                }}
                transition={{
                  duration: 50,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: 'reverse',
                }}
              />
            </div>

            <div className='container mx-auto relative z-10'>
              <motion.div
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={staggerContainer}
                className='text-center mb-16'
              >
                <motion.h2
                  variants={fadeInUp}
                  className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
                >
                  {t('why_list_with_xspace')}
                </motion.h2>
                <motion.div
                  variants={fadeInUp}
                  className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'
                />
                <motion.p
                  variants={fadeInUp}
                  className='text-gray-700 dark:text-gray-300 text-lg max-w-4xl mx-auto'
                >
                  {t('why_list_with_xspace_description')}
                </motion.p>
              </motion.div>

              {/* Service Cards */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mt-12'>
                {[
                  {
                    title: t('extensive_network'),
                    description: t('extensive_network_description'),
                    // description:
                    //   'We leverage our broad network of professional agents and marketing partners to give your property maximum exposure.',
                    icon: <Users className='h-8 w-8' />,
                  },
                  {
                    title: t('legal_expertise'),
                    description: t('legal_expertise_description'),
                    icon: <FileText className='h-8 w-8' />,
                  },
                  {
                    title: t('media_resources'),
                    description: t('media_resources_description'),
                    icon: (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-8 w-8'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                    ),
                  },
                ].map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow'
                  >
                    <div className='w-16 h-16 rounded-full bg-gradient-to-r from-primarycolor/20 to-secondarycolor/20 flex items-center justify-center mb-6 mx-auto text-primarycolor dark:text-secondarycolor'>
                      {card.icon}
                    </div>
                    <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                      {card.title}
                    </h3>
                    <p className='text-gray-600 dark:text-gray-400 text-center'>
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Real Estate Services Section */}
          <section className='py-20 px-4'>
            <div className='container mx-auto'>
              <motion.div
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={staggerContainer}
                className='text-center mb-16'
              >
                <motion.h2
                  variants={fadeInUp}
                  className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
                >
                  {t('real_estate_services')}
                </motion.h2>
                <motion.div
                  variants={fadeInUp}
                  className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'
                />
              </motion.div>

              {/* Services Grid */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                {/* BUY */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 group'
                >
                  <div className='w-16 h-16 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-6 mx-auto text-green-600 dark:text-emerald-400 group-hover:scale-110 transition-transform'>
                    <Home className='h-8 w-8' />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                    {t('service_buy')}
                  </h3>
                  <h4 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 text-center'>
                    {t('service_buy_title')}
                  </h4>
                  <p className='text-gray-600 dark:text-gray-400 text-center mb-6'>
                    {t('service_buy_description')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => router.push('/properties/buy')}
                    className='w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full font-medium flex items-center justify-center group'
                  >
                    {t('learnMore')}
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                  </motion.button>
                </motion.div>

                {/* SELL */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 group'
                >
                  <div className='w-16 h-16 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-6 mx-auto text-blue-600 dark:text-cyan-400 group-hover:scale-110 transition-transform'>
                    <DollarSign className='h-8 w-8' />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                    {t('service_sell')}
                  </h3>
                  <h4 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 text-center'>
                    {t('service_sell_title')}
                  </h4>
                  <p className='text-gray-600 dark:text-gray-400 text-center mb-6'>
                    {t('service_sell_description')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      router.push('/services/sell-property', undefined, {
                        scroll: false,
                      });
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 50);
                    }}
                    className='w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full font-medium flex items-center justify-center group'
                  >
                    {t('learnMore')}
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                  </motion.button>
                </motion.div>

                {/* RENT */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 group'
                >
                  <div className='w-16 h-16 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-6 mx-auto text-purple-600 dark:text-pink-400 group-hover:scale-110 transition-transform'>
                    <Key className='h-8 w-8' />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                    {t('service_rent')}
                  </h3>
                  <h4 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 text-center'>
                    {t('service_rent_title')}
                  </h4>
                  <p className='text-gray-600 dark:text-gray-400 text-center mb-6'>
                    {t('service_rent_description')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => router.push('/properties/rent')}
                    className='w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium flex items-center justify-center group'
                  >
                    {t('learnMore')}
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Golden Visa & Mortgage Services Section */}
          <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
            <div className='container mx-auto'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
                {/* Golden Visa Services */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700'
                >
                  <div className='w-16 h-16 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center mb-6 text-yellow-600 dark:text-orange-400'>
                    <Award className='h-8 w-8' />
                  </div>
                  <div className='inline-block px-4 py-2 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full text-yellow-600 dark:text-orange-400 font-medium mb-4'>
                    {t('residency_solutions')}
                  </div>
                  <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                    {t('residency_solutions_title_1')}
                  </h2>
                  <h3 className='text-xl text-gray-700 dark:text-gray-300 mb-4'>
                    {t('residency_solutions_title_2')}
                  </h3>
                  <p className='text-gray-600 dark:text-gray-400 mb-6'>
                    {t('residency_solutions_description')}
                  </p>

                  <div className='space-y-3 mb-6'>
                    {[
                      t('property_investment_guidance'),
                      t('documentation_support'),
                      t('application_processing'),
                      t('legal_compliance'),
                    ].map((feature, index) => (
                      <div key={index} className='flex items-center'>
                        <div className='w-5 h-5 rounded-full bg-yellow-500/10 dark:bg-orange-500/20 flex items-center justify-center mr-3 ml-3'>
                          <Check className='w-3 h-3 text-yellow-600 dark:text-orange-400' />
                        </div>
                        <span className='text-gray-700 dark:text-gray-300'>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    // onClick={() => router.push('/services/golden-visa')}
                    onClick={() => {
                      router.push('/services/golden-visa', undefined, {
                        scroll: false,
                      });
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 50);
                    }}
                    className='px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full font-medium flex items-center group'
                  >
                    {t('learnMore')}
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                  </motion.button>
                </motion.div>

                {/* Mortgage Consultancy */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700'
                >
                  <div className='w-16 h-16 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-6 text-indigo-600 dark:text-purple-400'>
                    <FileText className='h-8 w-8' />
                  </div>
                  <div className='inline-block px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full text-indigo-600 dark:text-purple-400 font-medium mb-4'>
                    {t('financing_solutions')}
                  </div>
                  <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                    {t('financing_solutions_title_1')}
                  </h2>
                  <h3 className='text-xl text-gray-700 dark:text-gray-300 mb-4'>
                    {t('financing_solutions_title_2')}
                  </h3>
                  <p className='text-gray-600 dark:text-gray-400 mb-6'>
                    {t('financing_solutions_description')}
                  </p>

                  <div className='space-y-3 mb-6'>
                    {[
                      t('personalized_consultations'),
                      t('best_loan_options'),
                      t('financing_solutions'),
                      t('application_support'),
                    ].map((feature, index) => (
                      <div key={index} className='flex items-center'>
                        <div className='w-5 h-5 rounded-full bg-indigo-500/10 dark:bg-purple-500/20 flex items-center justify-center mr-3 ml-3'>
                          <Check className='w-3 h-3 text-indigo-600 dark:text-purple-400' />
                        </div>
                        <span className='text-gray-700 dark:text-gray-300'>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    // onClick={() => router.push('/services/mortgage-consultancy')}
                    onClick={() => {
                      router.push('/services/mortgage-consultancy', undefined, {
                        scroll: false,
                      });
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 50);
                    }}
                    className='px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full font-medium flex items-center group'
                  >
                    {t('learnMore')}
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </section>

          {/* HomeCare Section */}
          <section className='py-20 px-4'>
            <div className='container mx-auto'>
              <motion.div
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={staggerContainer}
                className='text-center mb-12'
              >
                <motion.h2
                  variants={fadeInUp}
                  className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
                >
                  Property Maintenance &amp; HomeCare
                </motion.h2>
                <motion.div
                  variants={fadeInUp}
                  className='w-24 h-1 bg-gradient-to-r from-teal-700 to-teal-500 mx-auto mb-6'
                />
                <motion.p
                  variants={fadeInUp}
                  className='text-gray-700 dark:text-gray-300 text-lg max-w-3xl mx-auto'
                >
                  Professional property care and home support services for landlords,
                  tenants, homeowners and investors across Dubai.
                </motion.p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className='max-w-2xl mx-auto'
              >
                <div className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-teal-100 dark:border-teal-900/40 hover:shadow-2xl transition-all duration-300 group'>
                  <div className='flex items-center gap-4 mb-6'>
                    <div className='w-16 h-16 rounded-full bg-teal-700/10 flex items-center justify-center text-teal-700 dark:text-teal-400 group-hover:scale-110 transition-transform'>
                      <Wrench className='h-8 w-8' />
                    </div>
                    <div>
                      <span className='inline-block px-3 py-1 bg-teal-700/10 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded-full mb-1'>
                        New Service
                      </span>
                      <h3 className='text-2xl font-bold text-gray-900 dark:text-white'>
                        HomeCare
                      </h3>
                    </div>
                  </div>
                  <p className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3'>
                    Professional property maintenance and home support
                  </p>
                  <p className='text-gray-600 dark:text-gray-400 mb-6'>
                    Reliable AC maintenance, emergency repairs and complete property care
                    for landlords, tenants, homeowners and investors.
                  </p>
                  <Link
                    href='/services/home-care'
                    className='inline-flex items-center px-8 py-3 bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-full font-medium group/btn hover:from-teal-800 hover:to-teal-700 transition-all shadow-lg'
                    aria-label='Learn more about HomeCare services'
                  >
                    Learn More
                    <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover/btn:translate-x-1' />
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Call to Action Section */}
          <section className='py-20 px-4'>
            <div className='container mx-auto'>
              <div className='relative overflow-hidden rounded-3xl bg-gradient-to-r from-primarycolor/10 to-secondarycolor/10 dark:from-primarycolor/20 dark:to-secondarycolor/20 shadow-xl'>
                {/* Luxury pattern background */}
                <div className='absolute inset-0 opacity-5 dark:opacity-10'>
                  <motion.div
                    className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                    animate={{
                      backgroundPosition: ['0% 0%', '100% 100%'],
                    }}
                    transition={{
                      duration: 30,
                      repeat: Number.POSITIVE_INFINITY,
                      repeatType: 'reverse',
                    }}
                  />
                </div>

                {/* Gold corner accents */}
                <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl z-10'></div>
                <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-3xl z-10'></div>
                <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-3xl z-10'></div>
                <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] rounded-br-3xl z-10'></div>

                <div className='p-12 relative z-10 text-center'>
                  <motion.div
                    className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    {t('ready_to_experience')}
                  </motion.div>
                  <motion.p
                    className='text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                  >
                    {t('ready_to_experience_description')}
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    className='flex flex-col sm:flex-row justify-center gap-4'
                  >
                    <motion.button
                      whileHover={{
                        scale: 1.05,
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => router.push('/contact-us')}
                      className='px-8 py-4 bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor border-2 border-primarycolor dark:border-secondarycolor rounded-full font-medium hover:bg-primarycolor hover:text-white dark:hover:bg-secondarycolor dark:hover:text-black transition-colors shadow-lg'
                    >
                      {t('contact_us_today')}
                    </motion.button>
                    {/* <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  whileTap={{ scale: 0.95 }}
                  className='px-8 py-4 bg-gradient-to-r from-primarycolor to-secondarycolor text-white rounded-full font-medium hover:shadow-xl transition-shadow'
                >
                  Explore Our Services
                </motion.button> */}
                  </motion.div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

export default Services;
